import type { MessageEmbed } from '@nexo/types';
import { cn } from '@nexo/ui';
import { formatDay, formatTime } from '../../lib/format';
import { RichText } from './RichText';

const hex = (color: number) => `#${color.toString(16).padStart(6, '0')}`;

/** Only http(s) URLs are rendered from webhook payloads. */
const safeUrl = (url: string | undefined) => (url && /^https?:\/\//i.test(url) ? url : undefined);

/** Discord-style embed cards posted by integrations. */
export function MessageEmbeds({ embeds }: { embeds: MessageEmbed[] }) {
  if (embeds.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-col gap-2">
      {embeds.map((embed, index) => {
        const url = safeUrl(embed.url);
        const thumbnail = safeUrl(embed.thumbnail?.url);
        const image = safeUrl(embed.image?.url);
        return (
          <article
            key={index}
            className="flex max-w-lg gap-3 rounded-lg border border-l-4 border-border bg-surface-2/70 p-3"
            style={{
              borderLeftColor:
                embed.color !== undefined ? hex(embed.color) : 'var(--color-border-strong)',
            }}
          >
            <div className="min-w-0 flex-1">
              {embed.author && <p className="mb-1 text-xs font-semibold">{embed.author.name}</p>}
              {embed.title &&
                (url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block text-sm font-semibold text-accent hover:underline"
                  >
                    {embed.title}
                  </a>
                ) : (
                  <p className="text-sm font-semibold">{embed.title}</p>
                ))}
              {embed.description && (
                <p className="mt-1 text-[13px] leading-relaxed break-words whitespace-pre-wrap text-muted">
                  <RichText text={embed.description} />
                </p>
              )}
              {embed.fields && embed.fields.length > 0 && (
                <dl className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {embed.fields.map((field, i) => (
                    <div key={i} className={cn(!field.inline && 'sm:col-span-3')}>
                      <dt className="text-xs font-semibold">{field.name}</dt>
                      <dd className="text-[13px] break-words whitespace-pre-wrap text-muted">
                        <RichText text={field.value} />
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              {image && (
                <img src={image} alt="" loading="lazy" className="mt-2 max-h-72 rounded-md" />
              )}
              {(embed.footer || embed.timestamp) && (
                <p className="mt-2 text-[11px] text-subtle">
                  {embed.footer?.text}
                  {embed.footer && embed.timestamp && ' · '}
                  {embed.timestamp &&
                    `${formatDay(embed.timestamp)} ${formatTime(embed.timestamp)}`}
                </p>
              )}
            </div>
            {thumbnail && (
              <img
                src={thumbnail}
                alt=""
                loading="lazy"
                className="size-16 shrink-0 rounded-md object-cover"
              />
            )}
          </article>
        );
      })}
    </div>
  );
}
